// A buyer's first-order guides: a short "how ordering works" sheet the first
// time they are inside, then one-time callouts on the real controls as each
// first appears. All are remembered per device and never come back once done.
//
//  - intro:  the sheet — add items, choose a day (mind the cutoff), submit
//  - vendor: the vendor switcher — add or switch suppliers, the cart is kept
//  - review: Review Order after the first item — nothing is sent until submit
//  - cart:   the first full review — the delivery/pickup day and its deadline
//  - days:   the "Adding to" order bar — switch days, or start a separate
//            order for another day
//
// intro / vendor / review / cart are for a buyer who has never placed an order
// themselves. days is for a new buyer — at most one order of their own — the
// first time the order bar is there (it only appears once the buyer has an
// order, so a new buyer meets it right after their first). A buyer already
// ordering in the app never sees any of them (Jon, 2026-10-25).

export const FIRST_ORDER_TIPS = ["intro", "vendor", "review", "cart", "days"] as const;
export type FirstOrderTip = (typeof FIRST_ORDER_TIPS)[number];

/** Guides that end when the buyer's first order is sent. */
export const FIRST_ORDER_ONLY_TIPS: readonly FirstOrderTip[] = ["intro", "vendor", "review", "cart"];

const BROWSING_VIEWS = new Set(["categories", "items", "search"]);

/** Which guide belongs on screen right now, or null. One at a time. */
export function currentFirstOrderTip(state: {
  view: string;
  totalItems: number;
  drawer: "closed" | "quick-view" | "full-review";
  /** Orders the buyer placed themselves (staff-keyed orders don't count). */
  ownOrders: number;
  /** The vendor switcher is on screen (it is phone/tablet only). */
  vendorSwitcherShown: boolean;
  /** The "Adding to" order bar is on screen. */
  orderBarShown: boolean;
  seen: ReadonlySet<FirstOrderTip>;
}): FirstOrderTip | null {
  const { view, totalItems, drawer, ownOrders, vendorSwitcherShown, orderBarShown, seen } = state;
  const open = (tip: FirstOrderTip) => !seen.has(tip);
  const firstTimer = ownOrders === 0;

  if (drawer === "full-review") {
    return firstTimer && totalItems > 0 && open("cart") ? "cart" : null;
  }
  if (drawer !== "closed" || !BROWSING_VIEWS.has(view)) return null;

  if (firstTimer) {
    if (totalItems === 0) {
      if (open("intro")) return "intro";
      if (vendorSwitcherShown && open("vendor")) return "vendor";
    } else if (open("review")) {
      return "review";
    }
  }
  if (orderBarShown && ownOrders <= 1 && open("days")) return "days";
  return null;
}
